import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, Dimensions } from 'react-native';
import {
  Card,
  Text,
  Button,
  Chip,
  Surface,
  useTheme,
  ProgressBar,
  List,
  Avatar,
  Badge,
  Divider,
  IconButton,
  Portal,
  Dialog,
  TextInput,
  Slider,
  Switch,
} from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import {
  Brain,
  TrendingUp,
  Target,
  Clock,
  BookOpen,
  Users,
  Zap,
  CheckCircle,
  ArrowRight,
  Sparkles,
  BarChart3,
  Edit,
  Plus,
  Trash2,
  RefreshCw,
  Lightbulb,
  AlertCircle,
  Star,
  Calendar,
  Timer,
  Coffee,
  Filter,
  Search,
  BrainCircuit,
  Activity,
  Gauge,
} from 'lucide-react-native';

interface AdaptiveSession {
  id: string;
  title: string;
  type: 'practice' | 'assessment' | 'learning' | 'review';
  currentDifficulty: number;
  targetDifficulty: number;
  userPerformance: number;
  aiRecommendation: string;
  estimatedTime: number;
  isCompleted: boolean;
  metrics: AdaptiveMetrics;
  nextActivity: string;
}

interface AdaptiveMetrics {
  successRate: number;
  averageTime: number;
  engagementScore: number;
  difficultyRating: number;
  learningVelocity: number;
  retentionRate: number;
  cognitiveLoad: number;
}

interface DifficultyAdjustment {
  type: 'increase' | 'decrease' | 'maintain';
  reason: string;
  confidence: number;
  impact: 'high' | 'medium' | 'low';
  newDifficulty: number;
}

interface ContentRecommendation {
  id: string;
  title: string;
  type: 'video' | 'reading' | 'practice' | 'interactive';
  difficulty: number;
  estimatedTime: number;
  aiReasoning: string;
  priority: 'high' | 'medium' | 'low';
  prerequisites: string[];
}

const { width } = Dimensions.get('window');

export function AIAdaptiveLearningScreen() {
  const theme = useTheme();
  const insets = useSafeAreaInsets();
  const navigation = useNavigation();

  const [currentSession, setCurrentSession] = useState<AdaptiveSession | null>(
    null
  );
  const [showDifficultyDialog, setShowDifficultyDialog] = useState(false);
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [selectedContent, setSelectedContent] =
    useState<ContentRecommendation | null>(null);

  // Mock adaptive learning data
  const [adaptiveSessions, setAdaptiveSessions] = useState<AdaptiveSession[]>([
    {
      id: 'session-1',
      title: 'Espresso Extraction Practice',
      type: 'practice',
      currentDifficulty: 3,
      targetDifficulty: 4,
      userPerformance: 78,
      aiRecommendation:
        "Increase difficulty - you're performing above expectations",
      estimatedTime: 25,
      isCompleted: false,
      metrics: {
        successRate: 78,
        averageTime: 18,
        engagementScore: 85,
        difficultyRating: 3,
        learningVelocity: 0.23,
        retentionRate: 82,
        cognitiveLoad: 0.65,
      },
      nextActivity: 'Advanced Tamping Techniques',
    },
    {
      id: 'session-2',
      title: 'Grind Size Assessment',
      type: 'assessment',
      currentDifficulty: 2,
      targetDifficulty: 2,
      userPerformance: 65,
      aiRecommendation: 'Maintain difficulty - focus on fundamentals',
      estimatedTime: 15,
      isCompleted: true,
      metrics: {
        successRate: 65,
        averageTime: 22,
        engagementScore: 72,
        difficultyRating: 2,
        learningVelocity: 0.15,
        retentionRate: 68,
        cognitiveLoad: 0.45,
      },
      nextActivity: 'Water Temperature Control',
    },
    {
      id: 'session-3',
      title: 'Pour-Over Timing Mastery',
      type: 'learning',
      currentDifficulty: 4,
      targetDifficulty: 5,
      userPerformance: 92,
      aiRecommendation: 'Ready for advanced techniques - excellent progress',
      estimatedTime: 30,
      isCompleted: false,
      metrics: {
        successRate: 92,
        averageTime: 16,
        engagementScore: 94,
        difficultyRating: 4,
        learningVelocity: 0.31,
        retentionRate: 89,
        cognitiveLoad: 0.78,
      },
      nextActivity: 'Complex Pour Patterns',
    },
  ]);

  const [difficultyAdjustments, setDifficultyAdjustments] = useState<
    DifficultyAdjustment[]
  >([
    {
      type: 'increase',
      reason: 'User consistently exceeds time expectations',
      confidence: 0.87,
      impact: 'high',
      newDifficulty: 4,
    },
    {
      type: 'maintain',
      reason: 'Performance within optimal learning zone',
      confidence: 0.92,
      impact: 'medium',
      newDifficulty: 3,
    },
    {
      type: 'decrease',
      reason: 'Cognitive load too high for current level',
      confidence: 0.78,
      impact: 'high',
      newDifficulty: 2,
    },
  ]);

  const [contentRecommendations, setContentRecommendations] = useState<
    ContentRecommendation[]
  >([
    {
      id: 'content-1',
      title: 'Advanced Tamping Pressure Control',
      type: 'video',
      difficulty: 4,
      estimatedTime: 20,
      aiReasoning:
        "Based on your success in basic tamping, you're ready for advanced techniques",
      priority: 'high',
      prerequisites: ['Basic Tamping', 'Grind Consistency'],
    },
    {
      id: 'content-2',
      title: 'Water Chemistry Deep Dive',
      type: 'reading',
      difficulty: 3,
      estimatedTime: 25,
      aiReasoning:
        "Your interest in pour-over suggests you'd benefit from water science knowledge",
      priority: 'medium',
      prerequisites: ['Pour-Over Basics', 'Water Temperature'],
    },
    {
      id: 'content-3',
      title: 'Interactive Cupping Workshop',
      type: 'interactive',
      difficulty: 4,
      estimatedTime: 45,
      aiReasoning:
        'High engagement with tasting activities - perfect for skill development',
      priority: 'high',
      prerequisites: ['Basic Cupping', 'Flavor Recognition'],
    },
  ]);

  const handleStartSession = (session: AdaptiveSession) => {
    setCurrentSession(session);
  };

  const handleCompleteSession = async (
    sessionId: string,
    performance: number
  ) => {
    setIsAnalyzing(true);

    // Simulate AI analysis
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Update session with new metrics
    setAdaptiveSessions(prev =>
      prev.map(s =>
        s.id === sessionId
          ? { ...s, isCompleted: true, userPerformance: performance }
          : s
      )
    );

    // Generate new difficulty adjustment
    const newAdjustment: DifficultyAdjustment = {
      type:
        performance > 80
          ? 'increase'
          : performance < 60
            ? 'decrease'
            : 'maintain',
      reason:
        performance > 80
          ? 'Excellent performance - ready for more challenge'
          : performance < 60
            ? 'Need to reinforce fundamentals'
            : 'Performance in optimal range',
      confidence: 0.85 + Math.random() * 0.1,
      impact: 'medium',
      newDifficulty:
        performance > 80
          ? Math.min(5, s.currentDifficulty + 1)
          : performance < 60
            ? Math.max(1, s.currentDifficulty - 1)
            : s.currentDifficulty,
    };

    setDifficultyAdjustments(prev => [newAdjustment, ...prev]);
    setIsAnalyzing(false);
    setCurrentSession(null);
  };

  const getSessionTypeColor = (type: AdaptiveSession['type']) => {
    switch (type) {
      case 'practice':
        return theme.colors.primary;
      case 'assessment':
        return theme.colors.secondary;
      case 'learning':
        return theme.colors.tertiary;
      case 'review':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 2) return theme.colors.primary;
    if (difficulty <= 3) return theme.colors.secondary;
    if (difficulty <= 4) return theme.colors.tertiary;
    return theme.colors.error;
  };

  const getPerformanceColor = (performance: number) => {
    if (performance >= 80) return theme.colors.primary;
    if (performance >= 60) return theme.colors.secondary;
    return theme.colors.error;
  };

  const renderSessionCard = (session: AdaptiveSession) => (
    <Card
      key={session.id}
      style={styles.sessionCard}
      onPress={() => handleStartSession(session)}
    >
      <Card.Content>
        <View style={styles.sessionHeader}>
          <View style={styles.sessionTitleRow}>
            <Text variant='titleMedium' style={styles.sessionTitle}>
              {session.title}
            </Text>
            <View style={styles.sessionBadges}>
              <Chip
                mode='outlined'
                textStyle={{ color: getSessionTypeColor(session.type) }}
                style={{ borderColor: getSessionTypeColor(session.type) }}
                compact
              >
                {session.type}
              </Chip>
              <Chip
                mode='outlined'
                textStyle={{
                  color: getDifficultyColor(session.currentDifficulty),
                }}
                style={{
                  borderColor: getDifficultyColor(session.currentDifficulty),
                }}
                compact
              >
                Level {session.currentDifficulty}
              </Chip>
            </View>
          </View>
        </View>

        <View style={styles.sessionMetrics}>
          <View style={styles.metricRow}>
            <View style={styles.metricItem}>
              <Target size={16} color={theme.colors.onSurfaceVariant} />
              <Text variant='bodySmall' style={styles.metricText}>
                Target: Level {session.targetDifficulty}
              </Text>
            </View>
            <View style={styles.metricItem}>
              <Clock size={16} color={theme.colors.onSurfaceVariant} />
              <Text variant='bodySmall' style={styles.metricText}>
                {session.estimatedTime} min
              </Text>
            </View>
          </View>

          {session.isCompleted && (
            <View style={styles.performanceRow}>
              <Text variant='labelMedium'>Performance</Text>
              <Text
                variant='titleMedium'
                style={[
                  styles.performanceScore,
                  { color: getPerformanceColor(session.userPerformance) },
                ]}
              >
                {session.userPerformance}%
              </Text>
            </View>
          )}
        </View>

        <View style={styles.aiRecommendation}>
          <Brain size={16} color={theme.colors.primary} />
          <Text variant='bodySmall' style={styles.recommendationText}>
            {session.aiRecommendation}
          </Text>
        </View>

        <View style={styles.sessionActions}>
          {!session.isCompleted ? (
            <Button
              mode='contained'
              onPress={() => handleStartSession(session)}
              style={styles.actionButton}
              compact
            >
              Start Session
            </Button>
          ) : (
            <Button
              mode='outlined'
              onPress={() => handleStartSession(session)}
              style={styles.actionButton}
              compact
            >
              Review
            </Button>
          )}

          <Button
            mode='outlined'
            onPress={() => setShowDifficultyDialog(true)}
            style={styles.actionButton}
            compact
            icon='trending-up'
          >
            AI Analysis
          </Button>
        </View>
      </Card.Content>
    </Card>
  );

  const renderDifficultyAnalysis = () => (
    <Portal>
      <Dialog
        visible={showDifficultyDialog}
        onDismiss={() => setShowDifficultyDialog(false)}
        style={styles.dialog}
      >
        <Dialog.Title>AI Difficulty Analysis</Dialog.Title>

        <Dialog.Content style={styles.dialogContent}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <Surface style={styles.analysisCard} elevation={1}>
              <View style={styles.analysisHeader}>
                <BrainCircuit size={24} color={theme.colors.primary} />
                <Text variant='titleMedium' style={styles.analysisTitle}>
                  Real-time Adaptation
                </Text>
              </View>

              <Text variant='bodyMedium' style={styles.analysisDescription}>
                Our AI continuously monitors your performance and adjusts
                difficulty to maintain optimal learning conditions.
              </Text>
            </Surface>

            <Surface style={styles.adjustmentsCard} elevation={1}>
              <View style={styles.adjustmentsHeader}>
                <TrendingUp size={24} color={theme.colors.secondary} />
                <Text variant='titleMedium' style={styles.adjustmentsTitle}>
                  Recent Adjustments
                </Text>
              </View>

              {difficultyAdjustments.map((adjustment, index) => (
                <View key={index} style={styles.adjustmentItem}>
                  <View style={styles.adjustmentHeader}>
                    <Chip
                      mode='outlined'
                      textStyle={{ color: getAdjustmentColor(adjustment.type) }}
                      style={{
                        borderColor: getAdjustmentColor(adjustment.type),
                      }}
                      compact
                    >
                      {adjustment.type}
                    </Chip>
                    <Badge style={styles.confidenceBadge}>
                      {Math.round(adjustment.confidence * 100)}%
                    </Badge>
                  </View>

                  <Text variant='bodyMedium' style={styles.adjustmentReason}>
                    {adjustment.reason}
                  </Text>

                  <View style={styles.adjustmentDetails}>
                    <Chip
                      mode='outlined'
                      textStyle={{ color: getImpactColor(adjustment.impact) }}
                      style={{ borderColor: getImpactColor(adjustment.impact) }}
                      compact
                    >
                      {adjustment.impact} impact
                    </Chip>
                    <Text variant='bodySmall' style={styles.newDifficulty}>
                      New Level: {adjustment.newDifficulty}
                    </Text>
                  </View>
                </View>
              ))}
            </Surface>

            <Surface style={styles.metricsCard} elevation={1}>
              <View style={styles.metricsHeader}>
                <BarChart3 size={24} color={theme.colors.tertiary} />
                <Text variant='titleMedium' style={styles.metricsTitle}>
                  Learning Metrics
                </Text>
              </View>

              <View style={styles.metricsGrid}>
                <View style={styles.metricCard}>
                  <Text variant='labelSmall'>Learning Velocity</Text>
                  <Text variant='titleLarge' style={styles.metricValue}>
                    +0.23
                  </Text>
                  <Text variant='bodySmall' style={styles.metricTrend}>
                    Improving
                  </Text>
                </View>

                <View style={styles.metricCard}>
                  <Text variant='labelSmall'>Retention Rate</Text>
                  <Text variant='titleLarge' style={styles.metricValue}>
                    82%
                  </Text>
                  <Text variant='bodySmall' style={styles.metricTrend}>
                    Above Avg
                  </Text>
                </View>

                <View style={styles.metricCard}>
                  <Text variant='labelSmall'>Cognitive Load</Text>
                  <Text variant='titleLarge' style={styles.metricValue}>
                    0.65
                  </Text>
                  <Text variant='bodySmall' style={styles.metricTrend}>
                    Optimal
                  </Text>
                </View>

                <View style={styles.metricCard}>
                  <Text variant='labelSmall'>Engagement</Text>
                  <Text variant='titleLarge' style={styles.metricValue}>
                    85%
                  </Text>
                  <Text variant='bodySmall' style={styles.metricTrend}>
                    High
                  </Text>
                </View>
              </View>
            </Surface>
          </ScrollView>
        </Dialog.Content>

        <Dialog.Actions>
          <Button onPress={() => setShowDifficultyDialog(false)}>Close</Button>
          <Button
            mode='contained'
            onPress={() => {
              setShowDifficultyDialog(false);
              setShowContentDialog(true);
            }}
          >
            View Content Recommendations
          </Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );

  const renderContentRecommendations = () => (
    <Portal>
      <Dialog
        visible={showContentDialog}
        onDismiss={() => setShowContentDialog(false)}
        style={styles.dialog}
      >
        <Dialog.Title>AI Content Recommendations</Dialog.Title>

        <Dialog.Content style={styles.dialogContent}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <Surface style={styles.recommendationsCard} elevation={1}>
              <View style={styles.recommendationsHeader}>
                <Lightbulb size={24} color={theme.colors.secondary} />
                <Text variant='titleMedium' style={styles.recommendationsTitle}>
                  Personalized Content
                </Text>
              </View>

              <Text
                variant='bodyMedium'
                style={styles.recommendationsDescription}
              >
                Based on your learning patterns, performance, and preferences,
                here are the best next steps for your coffee journey.
              </Text>
            </Surface>

            {contentRecommendations.map(content => (
              <Surface
                key={content.id}
                style={styles.contentCard}
                elevation={1}
              >
                <View style={styles.contentHeader}>
                  <View style={styles.contentInfo}>
                    <Text variant='titleMedium' style={styles.contentTitle}>
                      {content.title}
                    </Text>
                    <View style={styles.contentBadges}>
                      <Chip mode='outlined' compact>
                        {content.type}
                      </Chip>
                      <Chip
                        mode='outlined'
                        textStyle={{
                          color: getDifficultyColor(content.difficulty),
                        }}
                        style={{
                          borderColor: getDifficultyColor(content.difficulty),
                        }}
                        compact
                      >
                        Level {content.difficulty}
                      </Chip>
                      <Chip
                        mode='outlined'
                        textStyle={{
                          color: getPriorityColor(content.priority),
                        }}
                        style={{
                          borderColor: getPriorityColor(content.priority),
                        }}
                        compact
                      >
                        {content.priority}
                      </Chip>
                    </View>
                  </View>

                  <View style={styles.contentMetrics}>
                    <Text variant='bodySmall'>{content.estimatedTime} min</Text>
                  </View>
                </View>

                <Text variant='bodyMedium' style={styles.contentReasoning}>
                  {content.aiReasoning}
                </Text>

                {content.prerequisites.length > 0 && (
                  <View style={styles.prerequisites}>
                    <Text
                      variant='labelSmall'
                      style={styles.prerequisitesTitle}
                    >
                      Prerequisites:
                    </Text>
                    <View style={styles.prerequisitesList}>
                      {content.prerequisites.map((prereq, index) => (
                        <Chip
                          key={index}
                          mode='outlined'
                          compact
                          style={styles.prereqChip}
                        >
                          {prereq}
                        </Chip>
                      ))}
                    </View>
                  </View>
                )}

                <Button
                  mode='contained'
                  onPress={() => {
                    setSelectedContent(content);
                    setShowContentDialog(false);
                  }}
                  style={styles.startContentButton}
                  compact
                >
                  Start Learning
                </Button>
              </Surface>
            ))}
          </ScrollView>
        </Dialog.Content>

        <Dialog.Actions>
          <Button onPress={() => setShowContentDialog(false)}>Close</Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );

  const getAdjustmentColor = (type: DifficultyAdjustment['type']) => {
    switch (type) {
      case 'increase':
        return theme.colors.primary;
      case 'decrease':
        return theme.colors.error;
      case 'maintain':
        return theme.colors.secondary;
      default:
        return theme.colors.outline;
    }
  };

  const getImpactColor = (impact: DifficultyAdjustment['impact']) => {
    switch (impact) {
      case 'high':
        return theme.colors.primary;
      case 'medium':
        return theme.colors.secondary;
      case 'low':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  const getPriorityColor = (priority: ContentRecommendation['priority']) => {
    switch (priority) {
      case 'high':
        return theme.colors.error;
      case 'medium':
        return theme.colors.secondary;
      case 'low':
        return theme.colors.outline;
      default:
        return theme.colors.outline;
    }
  };

  return (
    <View
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      {/* Info Header */}
      <Surface style={styles.header} elevation={2}>
        <View style={styles.headerContent}>
          <View style={styles.headerTitle}>
            <BrainCircuit size={32} color={theme.colors.primary} />
            <View style={styles.titleContainer}>
              <Text variant='headlineMedium' style={styles.title}>
                Adaptive Learning
              </Text>
              <Text variant='bodyMedium' style={styles.subtitle}>
                AI-powered difficulty adjustment and content optimization
              </Text>
            </View>
          </View>
        </View>
      </Surface>

      {/* Quick Stats */}
      <Surface style={styles.statsCard} elevation={1}>
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <Activity size={20} color={theme.colors.primary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              Active Sessions
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {adaptiveSessions.filter(s => !s.isCompleted).length}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Gauge size={20} color={theme.colors.secondary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              Avg Difficulty
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {(
                adaptiveSessions.reduce(
                  (sum, s) => sum + s.currentDifficulty,
                  0
                ) / adaptiveSessions.length
              ).toFixed(1)}
            </Text>
          </View>
          <View style={styles.statItem}>
            <TrendingUp size={20} color={theme.colors.tertiary} />
            <Text variant='labelSmall' style={styles.statLabel}>
              Performance
            </Text>
            <Text variant='titleMedium' style={styles.statValue}>
              {Math.round(
                adaptiveSessions
                  .filter(s => s.isCompleted)
                  .reduce((sum, s) => sum + s.userPerformance, 0) /
                  Math.max(
                    1,
                    adaptiveSessions.filter(s => s.isCompleted).length
                  )
              )}
              %
            </Text>
          </View>
        </View>
      </Surface>

      {/* Sessions List */}
      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
      >
        <Text variant='titleLarge' style={styles.sectionTitle}>
          Learning Sessions
        </Text>

        {adaptiveSessions.map(renderSessionCard)}
      </ScrollView>

      {/* Difficulty Analysis Dialog */}
      {renderDifficultyAnalysis()}

      {/* Content Recommendations Dialog */}
      {renderContentRecommendations()}

      {/* Selected Content Dialog */}
      {selectedContent && (
        <Portal>
          <Dialog
            visible={!!selectedContent}
            onDismiss={() => setSelectedContent(null)}
          >
            <Dialog.Title>Start Learning</Dialog.Title>
            <Dialog.Content>
              <Text variant='bodyMedium'>
                Ready to start "{selectedContent.title}"? This content was
                specifically recommended by our AI based on your learning
                patterns.
              </Text>
            </Dialog.Content>
            <Dialog.Actions>
              <Button onPress={() => setSelectedContent(null)}>Cancel</Button>
              <Button
                mode='contained'
                onPress={() => {
                  setSelectedContent(null);
                  // Navigate to content or start session
                }}
              >
                Begin
              </Button>
            </Dialog.Actions>
          </Dialog>
        </Portal>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 16,
    backgroundColor: theme => theme.colors.surface,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  titleContainer: {
    marginLeft: 16,
    flex: 1,
  },
  title: {
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
  },
  statsCard: {
    padding: 16,
    margin: 16,
    borderRadius: 12,
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statLabel: {
    marginTop: 4,
    opacity: 0.7,
  },
  statValue: {
    marginTop: 2,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    padding: 16,
    paddingBottom: 32,
  },
  sectionTitle: {
    marginBottom: 16,
    fontWeight: 'bold',
  },
  sessionCard: {
    marginBottom: 16,
    borderRadius: 12,
  },
  sessionHeader: {
    marginBottom: 16,
  },
  sessionTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  sessionTitle: {
    flex: 1,
    marginRight: 12,
    fontWeight: 'bold',
  },
  sessionBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  sessionMetrics: {
    marginBottom: 16,
  },
  metricRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  metricItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metricText: {
    marginLeft: 6,
    opacity: 0.7,
  },
  performanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
  },
  performanceScore: {
    fontWeight: 'bold',
  },
  aiRecommendation: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#f0f8ff',
    borderRadius: 8,
  },
  recommendationText: {
    marginLeft: 8,
    opacity: 0.8,
    flex: 1,
  },
  sessionActions: {
    flexDirection: 'row',
    gap: 12,
  },
  actionButton: {
    flex: 1,
  },
  dialog: {
    maxHeight: '90%',
  },
  dialogContent: {
    maxHeight: 500,
  },
  analysisCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  analysisHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  analysisTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  analysisDescription: {
    opacity: 0.8,
    lineHeight: 20,
  },
  adjustmentsCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  adjustmentsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  adjustmentsTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  adjustmentItem: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  adjustmentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  confidenceBadge: {
    backgroundColor: theme => theme.colors.primary,
  },
  adjustmentReason: {
    marginBottom: 12,
    fontWeight: 'bold',
  },
  adjustmentDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  newDifficulty: {
    opacity: 0.7,
  },
  metricsCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  metricsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  metricsTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metricCard: {
    flex: 1,
    minWidth: '45%',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
  },
  metricValue: {
    marginTop: 4,
    fontWeight: 'bold',
    color: theme => theme.colors.primary,
  },
  metricTrend: {
    marginTop: 2,
    opacity: 0.7,
    fontSize: 12,
  },
  recommendationsCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  recommendationsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  recommendationsTitle: {
    marginLeft: 12,
    fontWeight: 'bold',
  },
  recommendationsDescription: {
    opacity: 0.8,
    lineHeight: 20,
  },
  contentCard: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 12,
  },
  contentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  contentInfo: {
    flex: 1,
    marginRight: 12,
  },
  contentTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  contentBadges: {
    flexDirection: 'row',
    gap: 8,
  },
  contentMetrics: {
    alignItems: 'flex-end',
  },
  contentReasoning: {
    marginBottom: 16,
    opacity: 0.8,
    lineHeight: 20,
  },
  prerequisites: {
    marginBottom: 16,
  },
  prerequisitesTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  prerequisitesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  prereqChip: {
    marginBottom: 4,
  },
  startContentButton: {
    marginTop: 8,
  },
});
